import React, { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import { type GlobalSideNavigationProps } from '../../common/types';

export interface Options
	extends Pick<
		GlobalSideNavigationProps,
		| 'app'
		| 'appLabel'
		| 'cloudId'
		| 'customizationStore'
		| 'disableMenuCustomization'
		| 'isShortcutsStatic'
		| 'networkStorageParams'
		| 'orgId'
		| 'userId'
	> {
	// Whether the bottom element is provided
	hasBottomElement?: boolean;
	// Whether level 1 drag & drop is enabled
	l1DragAndDropEnabled: boolean;
}

export const OptionsContext = createContext<Options | undefined>(undefined);

export type OptionsProviderProps = PropsWithChildren<Options>;

export function OptionsProvider({
	app,
	appLabel,
	children,
	cloudId,
	customizationStore,
	disableMenuCustomization,
	hasBottomElement = false,
	isShortcutsStatic,
	l1DragAndDropEnabled,
	networkStorageParams,
	orgId,
	userId,
}: OptionsProviderProps) {
	const options = useMemo(
		() => ({
			app,
			appLabel,
			cloudId,
			customizationStore,
			disableMenuCustomization,
			hasBottomElement,
			isShortcutsStatic,
			l1DragAndDropEnabled,
			networkStorageParams,
			orgId,
			userId,
		}),
		[
			app,
			appLabel,
			cloudId,
			customizationStore,
			disableMenuCustomization,
			hasBottomElement,
			isShortcutsStatic,
			l1DragAndDropEnabled,
			networkStorageParams,
			orgId,
			userId,
		],
	);

	return <OptionsContext.Provider value={options}>{children}</OptionsContext.Provider>;
}

export const useOptions = () => {
	const options = useContext(OptionsContext);

	invariant(options, 'useOptions must be used inside an initialized OptionsProvider');

	return options;
};
